import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { User, MapPin, Package, Plus, Trash2, Edit2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
    const { user, token } = useAuth();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [newAddress, setNewAddress] = useState({
        name: '',
        phone: '',
        city: '',
        address: '',
        postal_code: '',
        delivery_type: 'address'
    });

    useEffect(() => {
        fetchAddresses();
    }, []);

    const fetchAddresses = async () => {
        try {
            const response = await axios.get(`${API}/user/addresses`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAddresses(response.data);
        } catch (error) {
            console.error('Error fetching addresses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAddress = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/user/addresses`, newAddress, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Адресът е добавен');
            setShowAddDialog(false);
            setNewAddress({ name: '', phone: '', city: '', address: '', postal_code: '', delivery_type: 'address' });
            fetchAddresses();
        } catch (error) {
            toast.error('Грешка при добавяне на адрес');
        }
    };

    const handleDeleteAddress = async (addressId) => {
        if (!window.confirm('Сигурни ли сте, че искате да изтриете този адрес?')) return;
        
        try {
            await axios.delete(`${API}/user/addresses/${addressId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Адресът е изтрит');
            fetchAddresses();
        } catch (error) {
            toast.error('Грешка при изтриване на адрес');
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="container-main py-8">
                <h1 className="font-serif text-3xl md:text-4xl font-bold mb-8">Моят профил</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg border border-border p-6">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                                    {user?.picture ? (
                                        <img src={user.picture} alt="" className="w-full h-full rounded-full" />
                                    ) : (
                                        <User className="h-8 w-8 text-primary" />
                                    )}
                                </div>
                                <div>
                                    <p className="font-semibold">{user?.name}</p>
                                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                                </div>
                            </div>

                            <nav className="space-y-2">
                                <Link
                                    to="/profile"
                                    className="flex items-center gap-3 px-4 py-2 bg-primary/10 text-primary rounded-md font-medium"
                                >
                                    <User className="h-5 w-5" />
                                    Профил
                                </Link>
                                <Link
                                    to="/orders"
                                    className="flex items-center gap-3 px-4 py-2 text-foreground hover:bg-muted rounded-md transition-colors"
                                >
                                    <Package className="h-5 w-5" />
                                    Поръчки
                                </Link>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Profile Info */}
                        <div className="bg-white rounded-lg border border-border p-6">
                            <h2 className="font-serif text-xl font-bold mb-6">Лична информация</h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Име</Label>
                                    <p className="font-medium mt-1">{user?.name}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Имейл</Label>
                                    <p className="font-medium mt-1">{user?.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* Addresses */}
                        <div className="bg-white rounded-lg border border-border p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-serif text-xl font-bold">Адреси за доставка</h2>
                                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" data-testid="add-address-btn">
                                            <Plus className="h-4 w-4 mr-2" />
                                            Добави адрес
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Нов адрес</DialogTitle>
                                        </DialogHeader>
                                        <form onSubmit={handleAddAddress} className="space-y-4">
                                            <div>
                                                <Label htmlFor="name">Име</Label>
                                                <Input
                                                    id="name"
                                                    value={newAddress.name}
                                                    onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="phone">Телефон</Label>
                                                <Input
                                                    id="phone"
                                                    value={newAddress.phone}
                                                    onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="city">Град</Label>
                                                <Input
                                                    id="city"
                                                    value={newAddress.city}
                                                    onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="address">Адрес</Label>
                                                <Input
                                                    id="address"
                                                    value={newAddress.address}
                                                    onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="postal_code">Пощенски код</Label>
                                                <Input
                                                    id="postal_code"
                                                    value={newAddress.postal_code}
                                                    onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                                                />
                                            </div>
                                            <Button type="submit" className="w-full btn-primary">
                                                Запази адрес
                                            </Button>
                                        </form>
                                    </DialogContent>
                                </Dialog>
                            </div>

                            {loading ? (
                                <div className="animate-pulse space-y-3">
                                    {[1, 2].map(i => (
                                        <div key={i} className="h-24 bg-muted rounded-lg" />
                                    ))}
                                </div>
                            ) : addresses.length === 0 ? (
                                <div className="text-center py-8">
                                    <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground">Нямате запазени адреси</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {addresses.map((addr) => (
                                        <div
                                            key={addr.address_id}
                                            className="flex items-start justify-between p-4 border border-border rounded-lg"
                                        >
                                            <div className="flex items-start gap-3">
                                                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                                                <div>
                                                    <p className="font-medium">{addr.name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {addr.address}, {addr.city}
                                                        {addr.postal_code && `, ${addr.postal_code}`}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">{addr.phone}</p>
                                                    {addr.is_default && (
                                                        <span className="inline-block mt-2 text-xs bg-secondary/10 text-secondary px-2 py-1 rounded">
                                                            По подразбиране
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteAddress(addr.address_id)}
                                                className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                                                data-testid={`delete-address-${addr.address_id}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
